import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { useConfirm, useConfirmUnsaved } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { useForceUpdate } from 'hooks/useForceUpdate';
import {
  ScreenSize,
  UI_DATE_FORMAT_LONG,
  UI_DATE_TIME_FORMAT_LONG,
} from 'lib/constants';
import {
  formatPhoneNumberForApi,
  PHONE_NUMBER_VALIDATION_EXAMPLE,
} from 'lib/phoneNumber';
import { dateToIsoString, formatDate, isoDateStringToDate } from 'lib/riskbase';
import { smartText } from 'lib/str';
import { theme } from 'lib/theme';
import { cloneDeep, isEqual } from 'lodash';
import React from 'react';
import { View } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  ALERT,
  doEngageContactEvacuationInfoLoad,
  doEngageSaveContact,
  eventTypeEngageContactAdded,
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
} from 'store/actions';
import { TextField } from 'components/Field/TextField';
import { TextAreaField } from 'components/Field/TextAreaField/TextAreaField';
import {
  AddedViaSource,
  AddedViaSourceLabels,
  EngageFlatContact,
  FlatContactType,
  FlatContactTypes,
  ContactArchivedViaLabels,
} from 'types/Engage';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import {
  selectEngageFlat,
  selectEngagePropertiesItem,
} from 'store/remote/engageSelectors';
import { getFlatContactConfirmationInfo } from 'store/remote/engageUtils';
import { useSelector } from 'hooks/useSelector';
import { ButtonToggleGroup } from 'components/ButtonToggleGroup';
import { EngageFlatModalCommunicationsPage } from './EngageFlatModalCommunicationsPage';
import { createId } from '@paralleldrive/cuid2';
import { isEmail } from 'class-validator';
import {
  BlockManagerPropertyFeatureEnums,
  EngageContactInteractionLog,
  EngageContactInteractionType,
} from 'types/DTOs/BlockManagerDTOs';
import { doAddEvent, doRemoveEvent } from 'lib/events';
import { useScreenSize } from 'hooks/useScreenSize';
import { PageHeader } from 'components/PageHeader';
import { ScrollView } from 'components/ScrollView';
import { ICONS } from 'components/Icon';
import { IconButton } from 'components/IconButton';
import { Gap } from 'components/Gap';
import { GapMedium } from 'components/GapMedium';

type HandleSetContactType = (
  arg:
    | {
        isOwner: boolean;
        isResident: boolean;
        type?: never;
      }
    | { isOwner?: never; isResident?: never; type: FlatContactType },
) => void;

type EngageContactModalContact = Omit<
  EngageFlatContact,
  'interactionLogs' | 'hasSupportNeeds'
>;

type HandleChangeWithName = <N extends keyof EngageContactModalContact>(
  name: N,
  value: EngageContactModalContact[N],
  doForceUpdate?: boolean,
) => void;

const formatEvacuationInteractionCount = (
  count: number,
  interactionType: 'viewed' | 'edited',
) =>
  count === 1
    ? `${interactionType} once`
    : count === 2
      ? `${interactionType} twice`
      : `${interactionType} ${count} times`;

function normalizeContact(
  contact: EngageContactModalContact,
): EngageContactModalContact {
  const normalizedContact = { ...contact };
  if (normalizedContact.isOwner || !normalizedContact.isResident) {
    normalizedContact.isUnder16 = false;
  }
  if (normalizedContact.isUnder16) {
    normalizedContact.firstName = '';
    normalizedContact.lastName = '';
    normalizedContact.phone = '';
    normalizedContact.email = '';
  }

  // @todo actually set this when !isOwner && !isResident
  normalizedContact.contactType =
    normalizedContact.isOwner && normalizedContact.isResident
      ? FlatContactType.residentOwner
      : normalizedContact.isOwner
        ? FlatContactType.owner
        : normalizedContact.isResident
          ? FlatContactType.resident
          : normalizedContact.contactType;

  return normalizedContact;
}

export const EngageContactModal: React.FC<
  ModalScreenProps<ModalScreens.EngageContact>
> = ({ route, navigation }) => {
  const dispatch = useDispatch();
  const confirm = useConfirm();
  const confirmUnsaved = useConfirmUnsaved();
  const forceUpdate = useForceUpdate();

  const { key, contactId: incomingContactId } = route.params;

  const [contactId, setContactId] = React.useState<string | null>(
    incomingContactId,
  );
  const [activeTab, setActiveTab] = React.useState<'information' | 'messages'>(
    'information',
  );

  React.useEffect(() => {
    const event = doAddEvent(eventTypeEngageContactAdded, (action) => {
      setContactId(action.payload.contactId);
    });
    return () => {
      doRemoveEvent(event);
    };
  }, []);

  const {
    flatDetails: { name: flatName, id: flatId },
    property: { id: propertyId },
    contacts,
  } = useSelector((state) => selectEngageFlat(state, key))!;

  const property = useSelector((state) =>
    selectEngagePropertiesItem(state, key, propertyId),
  );
  const propertyFeatures = property?.features ?? [];
  const hasEvacuationInfoAccess = propertyFeatures.includes(
    BlockManagerPropertyFeatureEnums.accessevacuationinfo,
  );

  const contact = contactId
    ? contacts.find((c) => c.id === contactId)
    : undefined;

  const initValues = React.useMemo<EngageContactModalContact>(
    () =>
      contact
        ? {
            id: contact.id,
            firstName: contact.firstName,
            lastName: contact.lastName,
            email: contact.email,
            phone: contact.phone,
            isOwner: contact.isOwner,
            isResident: contact.isResident,
            isUnder16: contact.isUnder16,
            isPrimary: contact.isPrimary,
            internalNotes: contact.internalNotes,
            comments: contact.comments,
            contactType: contact.contactType,
            confirmedDate: contact.confirmedDate,
            confirmationExpiryDate: contact.confirmationExpiryDate ?? null,
            addedTimestamp: contact.addedTimestamp,
            addedVia: contact.addedVia,
            archivedTimestamp: contact.archivedTimestamp,
            archivedVia: contact.archivedVia,
            archivedReason: contact.archivedReason,
          }
        : {
            id: createId(),
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            isOwner: true,
            isResident: false,
            isUnder16: false,
            isPrimary: false,
            internalNotes: '',
            comments: '',
            contactType: FlatContactType.owner,
            confirmedDate: null,
            confirmationExpiryDate: null,
            addedTimestamp: dateToIsoString(new Date()),
            addedVia: AddedViaSource.app,
            archivedTimestamp: null,
            archivedVia: null,
            archivedReason: null,
          },
    [contact],
  );

  const changes = React.useRef(cloneDeep(initValues));
  // If ID is different the contact has changed, reset changes
  if (initValues.id !== changes.current.id) {
    changes.current = cloneDeep(initValues);
  }
  const checkIsDirty = React.useCallback(
    () => !isEqual(initValues, changes.current),
    [initValues],
  );
  const isDirty = React.useRef<boolean>(false);
  isDirty.current = checkIsDirty();

  const isNew = !contactId;
  const isArchived = !!changes.current.archivedTimestamp;

  const handleSetContactType: HandleSetContactType = ({
    isOwner,
    isResident,
    type,
  }) => {
    if (type) {
      changes.current.isOwner = false;
      changes.current.isResident = false;
      changes.current.contactType = type;
    } else {
      changes.current.isOwner = isOwner;
      changes.current.isResident = isResident;
      changes.current.contactType =
        isOwner && isResident
          ? FlatContactType.residentOwner
          : isOwner
            ? FlatContactType.owner
            : FlatContactType.resident;
    }
    forceUpdate();
  };

  const handleChangeWithName: HandleChangeWithName = React.useCallback(
    (name, value, doForceUpdate) => {
      changes.current[name] = value;
      if (doForceUpdate || isDirty.current !== checkIsDirty()) {
        forceUpdate();
      }
    },
    [checkIsDirty, forceUpdate],
  );

  const handleSetConfirmedContact = React.useCallback(
    (isConfirmed: boolean) => {
      changes.current.confirmedDate = isConfirmed
        ? dateToIsoString(new Date())
        : null;
      changes.current.confirmationExpiryDate = null;
      forceUpdate();
    },
    [forceUpdate],
  );

  const handleSave = React.useCallback(
    (options?: { actionAfterSave?: 'openEmergencyEvacuationDetails' }) => {
      const normalizedContact = normalizeContact(changes.current);
      const errors = [];

      if (
        !normalizedContact.isUnder16 &&
        (normalizedContact.firstName.trim() === '' ||
          normalizedContact.lastName.trim() === '')
      ) {
        errors.push('*First Name* and *Last Name* are required');
      }

      const formattedPhone = formatPhoneNumberForApi(normalizedContact.phone);
      if (normalizedContact.phone && !formattedPhone) {
        errors.push(`*Phone* is not valid. ${PHONE_NUMBER_VALIDATION_EXAMPLE}`);
      }
      normalizedContact.phone = formattedPhone ?? '';

      const trimmedEmail = (normalizedContact.email || '').trim();
      normalizedContact.email = trimmedEmail;
      if (normalizedContact.isPrimary) {
        if (!trimmedEmail || !isEmail(trimmedEmail)) {
          errors.push('A primary contact requires an *email* address');
        }
      } else if (trimmedEmail && !isEmail(trimmedEmail)) {
        errors.push('*Email* is not valid');
      }

      if (errors.length) {
        dispatch(
          ALERT({
            title: 'Contact Cannot Be Saved',
            text: errors.join('\n'),
          }),
        );
        return;
      }

      dispatch(
        doEngageSaveContact({
          key,
          propertyId,
          flatId,
          isNew,
          contact: normalizedContact,
          actionAfterSave: options?.actionAfterSave,
        }),
      );
    },
    [dispatch, flatId, isNew, key, propertyId],
  );

  const handleClose = React.useCallback(() => {
    if (isDirty.current) {
      confirmUnsaved({
        onSave: () => handleSave,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [confirmUnsaved, handleSave, dispatch]);

  const isDirtyVar = isDirty.current;
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);
  useEsc(handleClose);

  const handleOpenEvacuationInfo = React.useCallback(() => {
    if (isNew) {
      confirm({
        title: 'Create Contact',
        text: 'This contact hasn’t been created yet. Do you want to create them and open their emergency evacuation details?',
        confirmText: 'Create and Open',
        onConfirm: () => {
          handleSave({
            actionAfterSave: 'openEmergencyEvacuationDetails',
          });
        },
      });
    } else {
      dispatch(
        doEngageContactEvacuationInfoLoad({
          key,
          propertyId,
          flatId,
          contactId: changes.current.id,
          contactName:
            `${changes.current.firstName} ${changes.current.lastName}`.trim() ||
            'Contact',
        }),
      );
    }
  }, [confirm, dispatch, flatId, handleSave, isNew, key, propertyId]);

  return (
    <Page
      header={
        <>
          <PageHeader
            title={
              isNew
                ? 'New Contact'
                : isArchived
                  ? 'Archived Contact'
                  : 'Contact'
            }
            subtitle={flatName}
            onPressCancelButton={handleClose}
            onPressSaveButton={isArchived ? undefined : handleSave}
            buttons={
              <IconButton
                icon={ICONS.PaperPlaneLight}
                onPress={() =>
                  dispatch(
                    NAVIGATE_MODAL({
                      screen: ModalScreens.EngageSendCommunication,
                      params: {
                        key,
                        propertyId,
                        flatIds: [flatId],
                        contactId: changes.current.id,
                        mode: 'singleFlat',
                      },
                    }),
                  )
                }
              />
            }
          />
          {!isNew && (
            <ButtonToggleGroup
              buttons={[
                { text: 'Information', key: 'information' },
                { text: 'Messages', key: 'messages' },
              ]}
              active={activeTab}
              onActiveChange={(newActive) => {
                setTimeout(() => setActiveTab(newActive), 1);
                return false;
              }}
              containerStyle={{
                marginBottom: theme.padding,
                alignSelf: 'stretch',
                marginHorizontal: theme.padding,
              }}
            />
          )}
        </>
      }
    >
      <View
        style={[
          activeTab === 'information' ? { flex: 1 } : { display: 'none' },
        ]}
      >
        <EngageContactInfoPage
          handleSetContactType={handleSetContactType}
          handleChangeWithName={handleChangeWithName}
          handleSetConfirmedContact={handleSetConfirmedContact}
          handleOpenEvacuationInfo={handleOpenEvacuationInfo}
          changes={changes}
          isNew={isNew}
          hasEvacuationInfoAccess={hasEvacuationInfoAccess}
          evacuationInfoLogs={contact?.interactionLogs || []}
        />
      </View>
      {!isNew && (
        <View
          style={[activeTab === 'messages' ? { flex: 1 } : { display: 'none' }]}
        >
          <EngageFlatModalCommunicationsPage
            pageKey={key}
            contactId={changes.current.id}
            isActive={activeTab === 'messages'}
          />
        </View>
      )}
    </Page>
  );
};

const EngageContactInfoPage: React.FC<{
  handleSetContactType: HandleSetContactType;
  handleChangeWithName: HandleChangeWithName;
  handleSetConfirmedContact: (isConfirmed: boolean) => void;
  handleOpenEvacuationInfo: () => void;
  changes: React.RefObject<EngageContactModalContact>;
  isNew: boolean;
  hasEvacuationInfoAccess: boolean;
  evacuationInfoLogs: EngageContactInteractionLog[];
}> = ({
  handleSetContactType: incomingHandleSetContactType,
  handleChangeWithName,
  handleSetConfirmedContact,
  handleOpenEvacuationInfo,
  changes,
  isNew,
  hasEvacuationInfoAccess,
  evacuationInfoLogs,
}) => {
  const dispatch = useDispatch();
  const [showEvacuationInfoLogs, setShowEvacuationInfoLogs] =
    React.useState(false);
  const screenSize = useScreenSize();

  const isArchived = !!changes.current.archivedTimestamp;
  const evacuationInfoViewLogCount = evacuationInfoLogs.filter(
    (log) =>
      log.interactionType ===
      EngageContactInteractionType.flatContactViewEvacuationInfo,
  ).length;
  const evacuationInfoEditLogCount = evacuationInfoLogs.filter(
    (log) =>
      log.interactionType ===
      EngageContactInteractionType.flatContactEditEvacuationInfo,
  ).length;
  const evacuationInfoSummaryText =
    evacuationInfoLogs.length === 0
      ? 'Information has not been viewed or edited'
      : evacuationInfoViewLogCount > 0 && evacuationInfoEditLogCount > 0
        ? `Information ${formatEvacuationInteractionCount(
            evacuationInfoViewLogCount,
            'viewed',
          )} and ${formatEvacuationInteractionCount(
            evacuationInfoEditLogCount,
            'edited',
          )}`
        : evacuationInfoViewLogCount > 0
          ? `Information ${formatEvacuationInteractionCount(
              evacuationInfoViewLogCount,
              'viewed',
            )}`
          : `Information ${formatEvacuationInteractionCount(
              evacuationInfoEditLogCount,
              'edited',
            )}`;

  const handleSetContactType: typeof incomingHandleSetContactType = (arg) => {
    incomingHandleSetContactType(arg);
  };

  const collectDetails =
    changes.current.isOwner ||
    (changes.current.isResident && !changes.current.isUnder16) ||
    (!changes.current.isOwner && !changes.current.isResident);
  const collectResidentAge =
    changes.current.isResident && !changes.current.isOwner;
  const collectIsPrimary =
    changes.current.isOwner || !changes.current.isUnder16;
  const { confirmedAndInDate } = getFlatContactConfirmationInfo(
    changes.current,
  );
  const residentAgeCard = (
    <Card
      label="Resident Age"
      labelLinkText="Why is this important?"
      onLabelLinkPress={() => {
        dispatch(
          ALERT({
            title: 'Resident Age',
            text: smartText(
              'The Building Safety Act and Fire Safety (England) Regulations place additional responsibilities on the Responsible and Accountable People relating to residents aged 16 and over.\n\n' +
                "For data protection reasons, we don't collect names or contact details for people under 16. We only ask whether they might need help evacuating.\n\n" +
                'If a resident under 16 is also an owner, their details are collected. This is because owners have additional legal responsibilities under the Building Safety Act.',
            ),
          }),
        );
      }}
      explanation={
        collectResidentAge
          ? 'Contact details are only collected for residents 16 years or over'
          : 'Resident age is only relevant to a resident who is not an owner'
      }
      marginHorizontal={false}
      style={{ flex: 1 }}
    >
      <CardRow
        value="16 Years or Over"
        onPress={
          isArchived
            ? undefined
            : () =>
                handleChangeWithName(
                  'isUnder16',
                  !changes.current.isUnder16,
                  true,
                )
        }
        checkedValue={
          collectResidentAge ? !changes.current.isUnder16 : undefined
        }
        icon={!collectResidentAge ? ICONS.DashLight : undefined}
        disabled={!collectResidentAge}
      />
    </Card>
  );
  const confirmedContactCard = (
    <Card
      marginHorizontal={false}
      style={{ flex: 1 }}
      label="Confirmed Contact"
    >
      <CardRow
        checkedValue={confirmedAndInDate}
        label="Is Confirmed Contact"
        onPress={
          isArchived
            ? undefined
            : () => handleSetConfirmedContact(!confirmedAndInDate)
        }
        style={{ flex: 1 }}
      />
    </Card>
  );
  const relationshipToFlatLabel =
    changes.current.isOwner && changes.current.isResident
      ? FlatContactTypes['resident-owner'].name
      : changes.current.isOwner
        ? FlatContactTypes.owner.name
        : changes.current.isResident
          ? FlatContactTypes.resident.name
          : FlatContactTypes.other.name;
  const relationshipToFlatMenuOptions = [
    {
      text: 'Owner',
      onPress: () => handleSetContactType({ isOwner: true, isResident: false }),
    },
    {
      text: 'Owner & Resident',
      onPress: () =>
        handleSetContactType({
          isOwner: true,
          isResident: true,
        }),
    },
    {
      text: 'Resident',
      onPress: () => handleSetContactType({ isOwner: false, isResident: true }),
    },
  ];
  const relationshipToFlatCard = (
    <Card
      style={{ flex: 1 }}
      marginHorizontal={false}
      label="Relationship to Flat"
    >
      <CardRow
        value={relationshipToFlatLabel}
        menuOptions={isArchived ? undefined : relationshipToFlatMenuOptions}
        showMenuOnPress
        hideMenuButton
        navigationIcon={isArchived ? undefined : ICONS.CaretDownSolid}
        navigationIconSize={12}
      />
    </Card>
  );

  return (
    <ScrollView>
      <Gap>
        <ViewWithSeparators
          horizontal={screenSize !== ScreenSize.small}
          separatorColor="transparent"
          separatorMargin={
            screenSize !== ScreenSize.small ? theme.padding / 2 : theme.padding
          }
          marginHorizontal
        >
          {relationshipToFlatCard}
          {residentAgeCard}
        </ViewWithSeparators>

        {(collectResidentAge || collectDetails) && (
          <ViewWithSeparators
            horizontal={screenSize !== ScreenSize.small}
            separatorColor="transparent"
            separatorMargin={
              screenSize !== ScreenSize.small
                ? theme.padding / 2
                : theme.padding
            }
            marginHorizontal
          >
            {confirmedContactCard}
            <Card
              style={{ flex: 1 }}
              label="Primary Contact"
              explanation={
                collectIsPrimary
                  ? 'Most messages will go to primary contacts'
                  : 'A resident under 16 years cannot be a primary contact'
              }
              marginHorizontal={false}
            >
              <CardRow
                label="Is Primary Contact"
                labelAlignTop
                onPress={
                  isArchived
                    ? undefined
                    : () =>
                        handleChangeWithName(
                          'isPrimary',
                          !changes.current.isPrimary,
                          true,
                        )
                }
                checkedValue={
                  collectIsPrimary ? changes.current.isPrimary : undefined
                }
                icon={!collectIsPrimary ? ICONS.DashLight : undefined}
                disabled={!collectIsPrimary}
              />
            </Card>
          </ViewWithSeparators>
        )}

        {collectDetails && (
          <GapMedium>
            <ViewWithSeparators
              horizontal
              separatorColor="transparent"
              separatorMargin={theme.paddingHorizontalHalf}
              marginHorizontal
            >
              <Card
                style={{ flex: 1 }}
                label="First Name"
                required
                marginHorizontal={false}
              >
                {isArchived ? (
                  <CardRow value={changes.current.firstName} />
                ) : (
                  <TextField
                    value={changes.current.firstName}
                    containerStyle={{ flex: 1 }}
                    name="firstName"
                    onChangeWithName={handleChangeWithName}
                    disabled={!collectDetails}
                  />
                )}
              </Card>
              <Card
                style={{ flex: 1 }}
                label="Last Name"
                required
                marginHorizontal={false}
              >
                {isArchived ? (
                  <CardRow value={changes.current.lastName} />
                ) : (
                  <TextField
                    value={changes.current.lastName}
                    containerStyle={{ flex: 1 }}
                    name="lastName"
                    onChangeWithName={handleChangeWithName}
                    disabled={!collectDetails}
                  />
                )}
              </Card>
            </ViewWithSeparators>
            <ViewWithSeparators
              horizontal
              separatorColor="transparent"
              separatorMargin={theme.paddingHorizontalHalf}
              marginHorizontal
            >
              <Card
                label="Email"
                style={{ flex: 1 }}
                marginHorizontal={false}
                required={changes.current.isPrimary}
              >
                {isArchived ? (
                  <CardRow
                    value={changes.current.email}
                    placeholder="No Email"
                  />
                ) : (
                  <TextField
                    value={changes.current.email}
                    containerStyle={{ flex: 1 }}
                    placeholder="No Email"
                    name="email"
                    onChangeWithName={handleChangeWithName}
                    disabled={!collectDetails}
                  />
                )}
              </Card>
              <Card label="Phone" style={{ flex: 1 }} marginHorizontal={false}>
                {isArchived ? (
                  <CardRow
                    value={changes.current.phone}
                    placeholder="No Phone"
                  />
                ) : (
                  <TextField
                    value={changes.current.phone}
                    containerStyle={{ flex: 1 }}
                    placeholder="No Phone Number"
                    name="phone"
                    onChangeWithName={handleChangeWithName}
                    disabled={!collectDetails}
                  />
                )}
              </Card>
            </ViewWithSeparators>
          </GapMedium>
        )}

        {changes.current.isResident && (
          <Card label="Vulnerability, PEEP and PCFRA Management">
            <CardRow
              label="Manage contact's personal details"
              value={`These details are sensitive, viewing and editing is logged.${!hasEvacuationInfoAccess ? ' You do not have access.' : ''}`}
              labelAlignTop
              icon={ICONS.EyeSlashLight}
              onPress={handleOpenEvacuationInfo}
              disabled={isArchived || !hasEvacuationInfoAccess}
            />
            <CardRow
              label={evacuationInfoSummaryText}
              labelNumberOfLines={0}
              icon={
                evacuationInfoLogs.length > 0
                  ? ICONS.ChevronLeftLight
                  : undefined
              }
              iconStyle={[
                showEvacuationInfoLogs
                  ? {
                      transform: [{ rotate: '270deg' }],
                    }
                  : {
                      transform: [{ rotate: '180deg' }],
                    },
              ]}
              onPress={
                evacuationInfoLogs.length > 0
                  ? () => setShowEvacuationInfoLogs((current) => !current)
                  : undefined
              }
            />
            {showEvacuationInfoLogs &&
              evacuationInfoLogs.map((log, index) => {
                const logDate = isoDateStringToDate(log.createdDate);
                const interactionTypeText =
                  log.interactionType ===
                  EngageContactInteractionType.flatContactEditEvacuationInfo
                    ? 'Edited'
                    : log.interactionType ===
                        EngageContactInteractionType.flatContactViewEvacuationInfoDenied
                      ? 'Tried to view without permission'
                      : 'Viewed';

                return (
                  <CardRow
                    key={index}
                    label={log.name}
                    label2={`${log.email} - ${interactionTypeText}`}
                    value={formatDate(logDate, UI_DATE_TIME_FORMAT_LONG)}
                  />
                );
              })}
          </Card>
        )}

        <Card
          label="Notes"
          explanation="Notes are for internal use only and are not visible to contacts"
        >
          {isArchived ? (
            <CardRow
              placeholder="No Internal Notes"
              value={changes.current.internalNotes}
            />
          ) : (
            <TextAreaField
              placeholder="Internal Notes"
              name="internalNotes"
              value={changes.current.internalNotes}
              onChangeWithName={handleChangeWithName}
            />
          )}
        </Card>

        <SeparatorHorizontal />

        <Card
          explanation={
            !isNew
              ? 'Added ' +
                `${formatDate(isoDateStringToDate(changes.current.addedTimestamp), UI_DATE_FORMAT_LONG)} ` +
                `via ${AddedViaSourceLabels[changes.current.addedVia]}${
                  changes.current.archivedTimestamp &&
                  changes.current.archivedVia
                    ? '\nArchived ' +
                      `${formatDate(isoDateStringToDate(changes.current.archivedTimestamp), UI_DATE_FORMAT_LONG)} ` +
                      `via ${ContactArchivedViaLabels[changes.current.archivedVia]}`
                    : ''
                }`
              : ''
          }
        />
      </Gap>
    </ScrollView>
  );
};
